import React, { useContext, useEffect, useMemo, useState } from "react";
import { Toaster, toast } from "react-hot-toast";
import { GlobalContext } from "../../../Context/GlobalContext";
import { Building2, RefreshCcw, Search, Filter } from "lucide-react";
import TicketDetailDrawer from "./TicketDetailDrawer";
import { StatusBadge, PriorityBadge } from "./TicketBadges";

import useSupport from "../../Services/SupportServices/useSupport";

const PER_PAGE = 20;

export default function AdminSupportTickets() {
  const {getAllSupportTickets, }=useSupport();

  const { api, adminId } = useContext(GlobalContext);

  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(false);

  // filters
  const [search, setSearch] = useState("");
  const [deptFilter, setDeptFilter] = useState("ALL");
  const [statusFilter, setStatusFilter] = useState("ALL"); // O / P / C / ALL
  const [priorityFilter, setPriorityFilter] = useState("ALL");

  // paging
  const [page, setPage] = useState(1);

  // drawer
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [activeTicket, setActiveTicket] = useState(null);

  const fetchTickets = async (
    silent = false
  ) => {
    try {
      if (!silent) setLoading(true);

      const result =
        await getAllSupportTickets(
          adminId
        );

      if (result.success) {
        setTickets(result.data);
      } else {
        toast.error(result.message);
        setTickets([]);
      }
    } catch (e) {
      toast.error("Error loading tickets");
      setTickets([]);
    } finally {
      if (!silent) setLoading(false);
    }
  };

  useEffect(() => {
    fetchTickets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adminId]);

  // derived options
  const departments = useMemo(() => {
    const s = new Set();
    tickets.forEach((t) => t.department && s.add(t.department));
    return ["ALL", ...Array.from(s)];
  }, [tickets]);
  const statusOptions = ["ALL", "O", "P", "C"];
  const priorityOptions = useMemo(() => {
    const s = new Set();
    tickets.forEach((t) => t.priority && s.add(t.priority));
    return ["ALL", ...Array.from(s)];
  }, [tickets]);

  // filtering
  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return tickets.filter((t) => {
      const inSearch =
        !q ||
        [t.ticket_code, t.subject, t.description, t.department, t.priority, t.status, t.customer_id]
          .map((x) => String(x || "").toLowerCase())
          .some((s) => s.includes(q));

      const byDept = deptFilter === "ALL" || t.department === deptFilter;
      const byStatus = statusFilter === "ALL" || t.status === statusFilter;
      const byPriority = priorityFilter === "ALL" || t.priority === priorityFilter;
      return inSearch && byDept && byStatus && byPriority;
    });
  }, [tickets, search, deptFilter, statusFilter, priorityFilter]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PER_PAGE));
  const pageRows = useMemo(
    () => filtered.slice((page - 1) * PER_PAGE, page * PER_PAGE),
    [filtered, page]
  );

  const openTicket = (t) => {
    setActiveTicket(t);
    setDrawerOpen(true);
  };
  const closeDrawer = (refetch = false) => {
    setDrawerOpen(false);
    setActiveTicket(null);
    if (refetch) fetchTickets(true);
  };

  return (
    <div className="p-3 lg:p-5">
      <Toaster position="top-right" />

      {/* Header */}
      <div className="mb-4 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <div className="grid h-10 w-10 place-items-center rounded-xl bg-[#01C37D]/10 text-[#01C37D]">
            <Building2 size={18} />
          </div>
          <div>
            <h1 className="text-xl font-semibold">Support Tickets</h1>
            <p className="text-sm text-gray-500">Track, filter, and respond fast</p>
          </div>
        </div>
        <button
          onClick={() => fetchTickets()}
          disabled={loading}
          className="inline-flex h-9 items-center gap-2 rounded-xl border px-3 text-sm hover:bg-gray-50"
        >
          <RefreshCcw size={16} />
          {loading ? "Refreshing…" : "Refresh"}
        </button>
      </div>

      {/* Filters */}
      <div className="rounded-2xl border bg-white p-3">
        <div className="mb-2 flex items-center gap-2 text-sm font-semibold text-gray-700">
          <Filter size={16} />
          Filters
        </div>
        <div className="flex flex-wrap items-end gap-2">
          <label className="text-sm">
            <div className="mb-1 text-xs text-gray-500">Search</div>
            <div className="flex h-10 w-80 items-center gap-2 rounded-xl border px-3">
              <Search size={16} className="text-gray-500" />
              <input
                className="h-full w-full text-sm outline-none"
                placeholder="Code / subject / description / customer…"
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
              />
            </div>
          </label>

          <label className="text-sm">
            <div className="mb-1 text-xs text-gray-500">Department</div>
            <select
              className="h-10 w-48 rounded-xl border bg-white px-3 text-sm outline-none"
              value={deptFilter}
              onChange={(e) => {
                setDeptFilter(e.target.value);
                setPage(1);
              }}
            >
              {departments.map((d) => (
                <option key={d} value={d}>{d}</option>
              ))}
            </select>
          </label>

          <label className="text-sm">
            <div className="mb-1 text-xs text-gray-500">Status</div>
            <select
              className="h-10 w-40 rounded-xl border bg-white px-3 text-sm outline-none"
              value={statusFilter}
              onChange={(e) => {
                setStatusFilter(e.target.value);
                setPage(1);
              }}
            >
              {statusOptions.map((s) => (
                <option key={s} value={s}>
                  {s === "ALL" ? "ALL" : s === "O" ? "Open" : s === "P" ? "In Progress" : "Closed"}
                </option>
              ))}
            </select>
          </label>

          <label className="text-sm">
            <div className="mb-1 text-xs text-gray-500">Priority</div>
            <select
              className="h-10 w-40 rounded-xl border bg-white px-3 text-sm outline-none"
              value={priorityFilter}
              onChange={(e) => {
                setPriorityFilter(e.target.value);
                setPage(1);
              }}
            >
              {priorityOptions.map((p) => (
                <option key={p} value={p}>{p}</option>
              ))}
            </select>
          </label>

          <div className="ml-auto flex gap-2">
            <button
              className="h-10 rounded-xl border px-3 text-sm"
              onClick={() => {
                setSearch(""); setDeptFilter("ALL"); setStatusFilter("ALL"); setPriorityFilter("ALL"); setPage(1);
              }}
            >
              Clear
            </button>
            <button className="h-10 rounded-xl bg-[#01C37D] px-4 text-sm font-medium text-white" disabled>
              Apply
            </button>
          </div>
        </div>
      </div>

      {/* Table */}
      <div className="mt-3 overflow-hidden rounded-2xl border bg-white">
        <div className="overflow-auto">
          <table className="min-w-full table-fixed text-left text-sm">
            <colgroup>
              <col className="w-24" />
              <col className="w-[28%]" />
              <col className="w-[12%]" />
              <col className="w-[12%]" />
              <col className="w-[12%]" />
              <col className="w-[12%]" />
              <col className="w-[12%]" />
              <col className="w-[12%]" />
            </colgroup>
            <thead className="bg-[#01C37D] text-white">
              <tr className="whitespace-nowrap">
                {["Code","Subject","Department","Priority","Status","Created","Updated","Action"].map((h) => (
                  <th key={h} className="px-4 py-2 font-semibold">{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={8} className="px-4 py-8 text-center text-gray-500">Loading…</td></tr>
              ) : pageRows.length === 0 ? (
                <tr><td colSpan={8} className="px-4 py-8 text-center text-gray-500">No tickets found.</td></tr>
              ) : (
                pageRows.map((t) => (
                  <tr key={t.pk_ticket_id} className="border-t align-top">
                    <td className="px-4 py-2 font-semibold">{t.ticket_code}</td>
                    <td className="px-4 py-2">
                      <div className="truncate">{t.subject}</div>
                      <div className="text-xs text-gray-500 line-clamp-2">{t.description}</div>
                    </td>
                    <td className="px-4 py-2">{t.department || "—"}</td>
                    <td className="px-4 py-2"><PriorityBadge value={t.priority} /></td>
                    <td className="px-4 py-2"><StatusBadge code={t.status} /></td>
                    <td className="px-4 py-2">{t.created_on || "—"}</td>
                    <td className="px-4 py-2">{t.updated_on || "—"}</td>
                    <td className="px-4 py-2">
                      <button
                        className="inline-flex items-center justify-center rounded-xl bg-[#01C37D] px-3 py-1.5 text-xs font-medium text-white hover:bg-[#01C37D]/90"
                        onClick={() => openTicket(t)}
                      >
                        View
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {filtered.length > PER_PAGE && (
          <div className="flex items-center justify-center gap-2 p-3">
            <button
              className="h-9 rounded-xl border px-3 text-sm"
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page <= 1}
            >
              Prev
            </button>
            <span className="text-sm text-gray-600">Page <b>{page}</b> of <b>{totalPages}</b></span>
            <button
              className="h-9 rounded-xl border px-3 text-sm"
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
            >
              Next
            </button>
          </div>
        )}
      </div>

      {/* Drawer */}
      <TicketDetailDrawer open={drawerOpen} onClose={closeDrawer} ticket={activeTicket} />
    </div>
  );
}
